import Bookmark from "@untitled-ui/icons-react/build/esm/Bookmark";
import FilterLines from "@untitled-ui/icons-react/build/esm/FilterLines";
import SearchSm from "@untitled-ui/icons-react/build/esm/SearchSm";
import { useCallback, useEffect, useRef, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import DropdownButton from "react-bootstrap/DropdownButton";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useSearchHistory } from "../../../../hooks";
import {
  doSearch,
  SEARCH_TYPES,
  setQueryValue as setQueryValueRedux,
  setSearchType,
} from "../../../../store/reducer/_searchReducer";
import {
  addError,
  showFilterModal,
} from "../../../../store/reducer/_uiReducer";
import {
  getAppliedFilters,
  getCurrentModelTypeAppliedFilters,
  getCurrentSearchPeriod,
  getPageTitle,
  getQueryValue,
  getSearchModelType,
  getSearchType,
  getSelectedPersonFilter,
} from "../../../../store/selectors";
import { ErrorMessage } from "../../../../utils/classes";
import {
  getStoredSearchQuery,
  getStoredSearchType,
} from "../../../../utils/_localStorage";
import { PDLButton } from "../../dna";
import { SearchHistoryDropdown } from "../_searchHistoryDropdown";
import "./index.scss";

const OmniBar = ({ headerClass = null, isMobile = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const searchType = useSelector(getSearchType);
  const pageTitle = useSelector(getPageTitle);
  const currentQueryValue = useSelector(getQueryValue);
  const appliedFilters = useSelector(getAppliedFilters);
  const modalTypeAppliedFilters = useSelector(
    getCurrentModelTypeAppliedFilters
  );
  const appliedModelType = useSelector(getSearchModelType);
  const currentPeriod = useSelector(getCurrentSearchPeriod);
  const selectedPersonFilter = useSelector(getSelectedPersonFilter);

  const [queryValue, setQueryValue] = useState(() => {
    const storedQuery = getStoredSearchQuery();
    return currentQueryValue || storedQuery || "";
  });
  const [showHistory, setShowHistory] = useState(false);
  const lastSyncedReduxValue = useRef(currentQueryValue);

  const { addToHistory, removeFromHistory, clearHistory, getFilteredHistory } =
    useSearchHistory();

  const history = getFilteredHistory(searchType?.id);

  const handleDDChange = useCallback(
    (value) => {
      dispatch(setSearchType(value));
    },
    [dispatch]
  );

  const handleInputChange = useCallback(({ target: { value = "" } = {} }) => {
    setQueryValue(value);
    setShowHistory(value?.length > 0);
  }, []);

  const handleSearch = useCallback(() => {
    if (queryValue?.length > 2) {
      dispatch(setQueryValueRedux(queryValue));
      addToHistory(queryValue, searchType.id);
      dispatch(doSearch({ query: queryValue, searchType }));
      navigate("/resultados");
    } else {
      const errorMessage = new ErrorMessage({
        message: "A pesquisa deve conter mais de 2 caracteres.",
        title: "Pesquisa Inválida",
        variant: "warning",
      });
      dispatch(addError(errorMessage));
    }
  }, [dispatch, navigate, queryValue, searchType, addToHistory]);

  const toggleFilterModal = useCallback(() => {
    dispatch(showFilterModal());
  }, [dispatch]);

  const renderSearchType = useCallback(() => searchType?.title, [searchType]);
  const currentSearchType = searchType.id;
  const currentAppliedFilterValues =
    appliedFilters[currentSearchType]?.flatMap((filter) => filter.values) ?? [];

  const modalTypeFilterCount =
    currentSearchType !== "modelos" || appliedModelType === "all"
      ? 0
      : modalTypeAppliedFilters?.length + 1;

  const filterCount =
    currentAppliedFilterValues?.length +
    (currentPeriod?.period && currentPeriod?.period !== "always" ? 1 : 0) +
    (selectedPersonFilter?.length > 2 ? 1 : 0) +
    modalTypeFilterCount;

  useEffect(() => {
    const storedQuery = getStoredSearchQuery();
    if (!currentQueryValue && storedQuery && storedQuery.trim().length > 0) {
      dispatch(setQueryValueRedux(storedQuery));
    }

    const storedSearchType = getStoredSearchType();
    if (storedSearchType && storedSearchType !== searchType.id) {
      dispatch(setSearchType(storedSearchType));
    }
  }, []);

  useEffect(() => {
    if (
      currentQueryValue &&
      typeof currentQueryValue === "string" &&
      currentQueryValue.trim().length > 0 &&
      currentQueryValue !== lastSyncedReduxValue.current
    ) {
      setQueryValue(currentQueryValue);
      lastSyncedReduxValue.current = currentQueryValue;
    } else if (!currentQueryValue || currentQueryValue === "") {
      lastSyncedReduxValue.current = currentQueryValue || "";
    }
  }, [currentQueryValue, queryValue]);

  return (
    <div className="pdl_component_omnibar w-100">
      {headerClass !== "home" && (
        <>
          {!isMobile && (
            <div className="header-filter-container d-flex flex-row align-items-center justify-contents-between gap-3">
              <div className="omnibar-search-wrapper position-relative">
                <InputGroup>
                  <DropdownButton
                    variant="outline-secondary"
                    title={renderSearchType()}
                    id="input-group-dropdown-1"
                  >
                    {Object.entries(SEARCH_TYPES)
                      .sort(({ order: a }, { order: b }) => a - b)
                      .map(([id, { title }]) => (
                        <Dropdown.Item
                          href="#"
                          key={`DD_ITEM_${title}`}
                          onClick={() => handleDDChange(id)}
                        >
                          {title}
                        </Dropdown.Item>
                      ))}
                  </DropdownButton>
                  <Form.Control
                    type="text"
                    placeholder={
                      (pageTitle && `Pesquisar ${pageTitle}`) ||
                      "Plataforma LEX"
                    }
                    value={queryValue || ""}
                    onChange={handleInputChange}
                    onFocus={() => {
                      if (queryValue && queryValue?.length > 0) {
                        setShowHistory(true);
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowHistory(false), 150);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        setShowHistory(false);
                        handleSearch();
                      }
                    }}
                  />
                  <PDLButton
                    variant="tertiary"
                    className="gray px-3"
                    onClick={handleSearch}
                    disabled={queryValue && queryValue?.length < 2}
                  >
                    <SearchSm />
                  </PDLButton>
                </InputGroup>
                <SearchHistoryDropdown
                  show={showHistory}
                  history={history}
                  currentSearchType={searchType.id}
                  onClear={clearHistory}
                  onRemove={removeFromHistory}
                  onSelectSearch={(query) => {
                    setQueryValue(query);
                    setShowHistory(false);
                    dispatch(setQueryValueRedux(query));
                    addToHistory(query, searchType.id);
                    dispatch(doSearch({ query, searchType }));
                    navigate("/resultados");
                  }}
                />
              </div>
              <div className="filter-buttons-container d-flex flex-row align-items-center gap-3">
                <PDLButton
                  variant="primary"
                  onClick={toggleFilterModal}
                  disabled={searchType.id === "all"}
                >
                  <FilterLines />
                  <span className="filter">
                    <p>Filtros</p>
                    {filterCount > 0 && (
                      <p className="count-box">{filterCount}</p>
                    )}
                  </span>
                </PDLButton>
                <PDLButton variant="secondary">
                  <Bookmark />
                  <span className="d-none d-sm-block">
                    &nbsp;Salvar&nbsp;Pesquisa
                  </span>
                </PDLButton>
              </div>
            </div>
          )}
          {isMobile && (
            <div className="omnibar-search-wrapper position-relative">
              <InputGroup>
                <DropdownButton
                  variant="outline-secondary"
                  title={renderSearchType()}
                  id="input-group-dropdown-mobile"
                  size="sm"
                >
                  {Object.entries(SEARCH_TYPES)
                    .sort(({ order: a }, { order: b }) => a - b)
                    .map(([id, { title }]) => (
                      <Dropdown.Item
                        href="#"
                        key={`DD_ITEM_MOBILE_${title}`}
                        onClick={() => handleDDChange(id)}
                      >
                        {title}
                      </Dropdown.Item>
                    ))}
                </DropdownButton>
                <Form.Control
                  type="text"
                  placeholder={
                    (pageTitle && `Pesquisar ${pageTitle}`) || "Plataforma LEX"
                  }
                  value={queryValue || ""}
                  onChange={handleInputChange}
                  size="sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                />
                <PDLButton
                  variant="tertiary"
                  className="gray px-2"
                  onClick={handleSearch}
                  size="sm"
                  disabled={queryValue && queryValue?.length < 2}
                >
                  <SearchSm />
                </PDLButton>
              </InputGroup>
              <SearchHistoryDropdown
                show={showHistory}
                history={history}
                currentSearchType={searchType.id}
                onClear={clearHistory}
                onRemove={removeFromHistory}
                onSelectSearch={(query) => {
                  setQueryValue(query);
                  setShowHistory(false);
                  dispatch(setQueryValueRedux(query));
                  addToHistory(query, searchType.id);
                  dispatch(doSearch({ query, searchType }));
                  navigate("/resultados");
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OmniBar;
